<template>
  <div class="d-flex flex-column" :style="{ gap: '10px' }">
    <!-- Toolbar -->
    <v-toolbar flat>
      <v-toolbar-title>Transactions for {{ consumer.name }}</v-toolbar-title>
      <v-spacer />
      <v-btn text @click="$router.back()">
        <v-icon left>
          mdi-arrow-left
        </v-icon>
        Back
      </v-btn>
    </v-toolbar>

    <!-- Filters -->
    <div
      class="d-flex flex-column flex-sm-row mb-3"
      :style="{
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }"
    >
      <v-text-field
        v-model="tableFilters.search"
        outlined
        label="Search Transactions"
        dense
        clearable
        hide-details="auto"
        style="max-width: 250px"
      />

      <v-select
        v-model="tableFilters.type"
        :items="typeOptions"
        label="Filter by Type"
        outlined
        dense
        clearable
        multiple
        chips
        small-chips
        deletable-chips
        hide-details="auto"
        style="max-width: 250px"
      />

      <!-- Date Range -->
      <!-- Date Range -->
      <div class="d-flex flex-row" :style="{ gap: '10px' }">
        <v-text-field
          v-model="tableFilters.startDate"
          label="Start Date"
          type="date"
          outlined
          dense
          hide-details
          style="max-width: 150px"
        />
        <v-text-field
          v-model="tableFilters.endDate"
          label="End Date"
          type="date"
          outlined
          dense
          hide-details
          style="max-width: 150px"
        />
      </div>

      <v-btn text @click="resetFilters">
        Reset
      </v-btn>
    </div>

    <!-- Summary -->
    <div class="d-flex flex-row flex-wrap mb-3" :style="{ gap: '10px' }">
      <v-text-field
        hide-details
        label="Total Balance"
        :value="formatAccounting(summary.balance)"
        outlined
        dense
        readonly
        color="green"
        style="max-width: 250px"
      />
      <v-text-field
        hide-details
        label="Total Cash Transaction"
        :value="formatAccounting(summary.totalCashTransaction)"
        outlined
        dense
        readonly
        color="blue"
        style="max-width: 250px"
      />
    </div>
    <!-- Table -->
    <v-data-table
      :loading="table.loading"
      :headers="table.headers"
      :items="filteredItems"
      dense
      class="elevation-1"
      item-key="id"
    >
      <template #item.createdAt="{ value }">
        <td>{{ dateFormatWithTime(new Date(value), 'en-US') }}</td>
      </template>

      <template #item.actualDate="{ value }">
        <td class="text-center">
          {{ getCurrentDate(new Date(value), 'en-US') }}
        </td>
      </template>

      <template #item.type="{ value }">
        <td class="text-center">
          <v-chip
            :color="
              { cash: 'green', credit: 'blue', payment: 'purple' }[value] ||
                'grey'
            "
            dark
            small
          >
            {{ value }}
          </v-chip>
        </td>
      </template>

      <template #item.created_at="{ value }">
        <td>{{ dateFormatWithTime(new Date(value), 'en-US') }}</td>
      </template>

      <template #item.total_amount="{ value }">
        {{ formatAccounting(value) }}
      </template>

      <template #item.notes="{ item }">
        <td style="white-space: normal">
          {{ item.notes || '-' }}
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { authAxios } from '@/extra-functions/axios.extra'
import {
  formatAccounting,
  useWatchEffect,
  ErrorPopup,
  dateFormatWithTime,
  getCurrentDate,
  LoadingPopup,
} from '@/lib/utils'

const [watchEffect, clearEffect] = useWatchEffect()

export default {
  name: 'ConsumerTransactionsIndexV2',
  data() {
    return {
      dateMenu: false,
      table: {
        headers: [
          { text: 'Timestamp', value: 'created_at', width: '200px' },
          { text: 'Receipt #', value: 'receipt_no', width: '150px' },
          {
            text: 'Actual Date',
            value: 'actual_date',
            width: '150px',
            align: 'center',
          },
          {
            text: 'Type',
            value: 'type',
            align: 'center',
            width: '120px',
          },
          {
            text: 'Amount',
            value: 'total_amount',
            sortable: false,
            cellClass: 'text-end',
            width: '150px',
          },
          { text: 'Notes', value: 'notes', sortable: false },
        ],
        loading: false,
        items: [],
        total: 0,
      },
      consumer: {},
      tableFilters: {
        search: '',
        type: [],
        startDate: '',
        endDate: '',
      },
      typeOptions: ['credit', 'cash', 'payment'],
      summary: {
        balance: 0,
        totalCashTransaction: 0,
      },
    }
  },
  computed: {
    consumerId() {
      return this.$route.params.consumer
    },
    filteredItems() {
      let items = [...this.table.items]
      const { search, type, startDate, endDate } = this.tableFilters

      if (search) {
        const s = search.toLowerCase()
        items = items.filter(
          (item) =>
            (item.notes || '').toLowerCase().includes(s) ||
            (item.receiptNo || '').toLowerCase().includes(s),
        )
      }

      if (type.length) {
        items = items.filter((item) => type.includes(item.type))
      }

      if (startDate || endDate) {
        items = items.filter((item) => {
          const itemDate = new Date(item.actualDate)
          if (startDate && endDate) {
            const dStartDate = new Date(startDate)
            const dEndDate = new Date(endDate)
            const itemDate = new Date(item.actualDate)
            return itemDate >= dStartDate && itemDate <= dEndDate
          }
          if (startDate) {
            const dStartDate = new Date(startDate)
            return dStartDate <= itemDate
          }
          if (endDate) {
            const dEndDate = new Date(endDate)
            return dEndDate >= itemDate
          }
          return true
        })
      }

      return items
    },
  },
  created() {
    this.fetchTransactions()
  },
  destroyed() {
    clearEffect()
  },
  methods: {
    async fetchTransactions() {
      watchEffect(async (onCleanup) => {
        try {
          this.table.loading = true
          LoadingPopup.fire('Loading', 'Getting Transactions...')
          const controller = new AbortController()
          onCleanup(() => controller.abort())

          const response = await authAxios().get(
            `/v2/consumers/${this.consumerId}/transactions`,
            {
              signal: controller.signal,
            },
          )
          console.log('Transactions response:', response.data)
          this.table.items = response.data.data
          this.table.total = response.data.count
          this.consumer = response.data.consumer

          // set summary totals
          this.summary.balance = response.data.balance
          this.summary.totalCashTransaction =
            response.data.total_cash_transaction
          LoadingPopup.close()
        } catch (e) {
          ErrorPopup.fire('Error', 'Unable to fetch transactions')
          console.error(e)
        } finally {
          this.table.loading = false
        }
      }, `ConsumerTransactions_fetch_${this.consumerId}`)
    },
    resetFilters() {
      this.tableFilters.search = ''
      this.tableFilters.type = []
      this.tableFilters.startDate = null
      this.tableFilters.endDate = null
    },
    formatAccounting,
    getCurrentDate,
    dateFormatWithTime,
  },
}
</script>

<style scoped>
.v-data-table td {
  vertical-align: middle;
}
</style>
